import React from "react";
import { Download } from "lucide-react";

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@musa.tech/design-system";
import { useCollectMtrFile } from "@/hooks/useCollectMtrFile";

interface ModalCollectMtrFileProps {
  collectId: string | null;
  onOpenChange: (open: boolean) => void;
}

const MtrFilePanel: React.FC<{
  mtrUrl: string;
  isLoading: boolean;
  isError: boolean;
}> = ({ mtrUrl, isLoading, isError }) => {
  if (isLoading) {
    return <Skeleton className="w-full h-20 rounded-md" />;
  }

  if (isError) {
    return (
      <div className="w-full rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        Não foi possível carregar o arquivo MTR.
      </div>
    );
  }

  if (!mtrUrl) {
    return (
      <div className="w-full rounded-md bg-muted px-4 py-6 text-center text-sm text-muted-foreground">
        Nenhum arquivo MTR disponível para esta coleta.
      </div>
    );
  }

  return (
    <div className="h-[52vh] w-full overflow-hidden rounded-md border bg-white sm:h-[65vh]">
      <iframe
        src={mtrUrl}
        title="Arquivo MTR"
        className="h-full w-full"
      />
    </div>
  );
};

const ModalCollectMtrFile: React.FC<ModalCollectMtrFileProps> = ({
  collectId,
  onOpenChange,
}) => {
  const { data, isLoading, isError } = useCollectMtrFile(collectId);
  const mtrUrl = data?.mtr?.trim() ?? "";

  return (
    <Dialog open={!!collectId} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>MTR</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <MtrFilePanel mtrUrl={mtrUrl} isLoading={isLoading} isError={isError} />
        </div>

        <DialogFooter>
          {mtrUrl && (
            <a href={mtrUrl} target="_blank" rel="noopener noreferrer" download>
              <Button type="button" variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Baixar
              </Button>
            </a>
          )}
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Fechar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCollectMtrFile;
